import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { type ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { getQualityBand } from 'partner360/components/qualityIndicator'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import {
  formatCockpitMoney,
  formatCockpitPercent,
  getCockpitKeyInitiativesSummary,
} from '../../lib'
import { setCockpitInitiativesView } from '../../model'
import { useCockpitBaselineAndPlan, useCockpitKeyInitiativesData } from '../../model/hooks'
import {
  selectCockpitCategory,
  selectCockpitCurrency,
  selectCockpitInitiativesPlanQualitySummary,
  selectCockpitMetric,
  selectCockpitOffering,
  selectCockpitPeriods,
  selectCockpitPlanningPeriod,
  selectEffectiveCockpitFYear,
} from '../../model/selectors'
import { CockpitCollapsedMetric } from '../components/cockpitCollapsedMetric'
import { CockpitCollapsedMetricGroup } from '../components/cockpitCollapsedMetricGroup'
import { CockpitCollapsedSummary } from '../components/cockpitCollapsedSummary'

type InitiativeMetricProps = {
  label: ReactNode
  value: string | number
  coverage?: string
}

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS

const InitiativeMetric = ({ label, value, coverage }: InitiativeMetricProps) => (
  <CockpitCollapsedMetric label={label} value={value} secondaryValue={coverage} />
)

/**
 * The tile's collapsed content: how many initiatives the partner planned, what they add up to,
 * and how good the plan behind them looks — the same three figures plus Plan Quality the
 * wireframe's `#tile-init` shows before it is expanded. The MDF side of the tile lives in the
 * expanded view's MDF sub-tab.
 */
interface ICockpitKeyInitiativesSummaryProps {
  /** Expands the tile — handed down by the tile board through the collapsed view. */
  onExpand?: () => void
  isManagerCockpitMode?: boolean
}

export const CockpitKeyInitiativesSummary = ({
  onExpand,
  isManagerCockpitMode = false,
}: ICockpitKeyInitiativesSummaryProps) => {
  const dispatch = useAppDispatch()
  const { initiatives, mdfActivities } = useCockpitKeyInitiativesData({
    isManagerCockpitMode,
  })
  const selectedOffering = useSelector(selectCockpitOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const selectedPeriods = useSelector(selectCockpitPeriods)
  const selectedMetric = useSelector(selectCockpitMetric)
  const selectedCurrency = useSelector(selectCockpitCurrency)
  const planningPeriod = useSelector(selectCockpitPlanningPeriod)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const initiativesQuality = useSelector(selectCockpitInitiativesPlanQualitySummary)
  const qualityScore = initiativesQuality.selectedQuality?.overall.score ?? null
  const { baseline, plan } = useCockpitBaselineAndPlan()
  const hasLoadedData = planningPeriod !== null || initiatives.length > 0

  const summary = useMemo(
    () =>
      getCockpitKeyInitiativesSummary({
        initiatives,
        mdfActivities,
        fYear,
        selectedOffering,
        selectedCategory,
        selectedPeriods,
        selectedMetric,
        selectedCurrency,
        planningPeriod,
      }),
    [
      initiatives,
      mdfActivities,
      fYear,
      planningPeriod,
      selectedCurrency,
      selectedMetric,
      selectedOffering,
      selectedCategory,
      selectedPeriods,
    ]
  )

  const handleShowQuality = () => {
    dispatch(setCockpitInitiativesView('quality'))
    onExpand?.()
  }

  const qualityBand = qualityScore === null ? null : getQualityBand(qualityScore)
  const qualityTitle = qualityBand
    ? `Initiatives plan quality: ${qualityScore}/100 - ${qualityBand}.`
    : 'Initiatives plan quality is not available.'
  const formatMoney = (value: number | null) =>
    hasLoadedData ? formatCockpitMoney(value, selectedCurrency) : '--'

  /** Share of the financial plan (or, for the incremental goal, of the plan's own growth over
   *  baseline) the initiatives' goals already account for. Both denominators can be missing or
   *  non-positive — an unset measure, no plan yet, a plan below baseline — and then there is no
   *  ratio to show rather than a misleading one. */
  const formatCoverage = (goal: number | null, total: number | null, suffix: string) =>
    goal !== null && total !== null && total > 0
      ? `${formatCockpitPercent((goal / total) * 100)} of ${suffix}`
      : `-- of ${suffix}`

  const planGrowth =
    plan.sales !== null && baseline.sales !== null ? plan.sales - baseline.sales : null

  return (
    <CockpitCollapsedSummary
      quality={qualityScore}
      qualityLabel={<Translation text={KEYS.planQuality.title} span />}
      qualityTitle={`${qualityTitle} Click to see how it is scored.`}
      onQualityClick={handleShowQuality}
    >
      <CockpitCollapsedMetricGroup>
        <InitiativeMetric
          label={<Translation text={KEYS.keyInitiatives.summary.initiatives} span />}
          value={hasLoadedData ? summary.initiativesCount : '--'}
        />
        <InitiativeMetric
          label={<Translation text={KEYS.keyInitiatives.summary.financialGoal} span />}
          value={formatMoney(summary.financialGoal)}
          coverage={formatCoverage(summary.financialGoal, plan.sales, 'Plan')}
        />
        <InitiativeMetric
          label={<Translation text={KEYS.keyInitiatives.summary.incrementalGoal} span />}
          value={formatMoney(summary.incrementalGoal)}
          coverage={formatCoverage(summary.incrementalGoal, planGrowth, 'Growth')}
        />
        <InitiativeMetric
          label={<Translation text={KEYS.keyInitiatives.summary.mdfPlanned} span />}
          value={formatMoney(summary.mdfPlanned)}
        />
      </CockpitCollapsedMetricGroup>
    </CockpitCollapsedSummary>
  )
}

CockpitKeyInitiativesSummary.displayName = 'CockpitKeyInitiativesSummary'
